import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  GoodsReceiptMutationUpdatePalletRowArgs,
  GoodsReceiptPalletRow,
  GoodsReceiptPalletRowUpdateFailure,
  GoodsReceiptPalletRowUpdateInput,
  GoodsReceiptPalletRowUpdateSuccess,
  UpdateGoodsReceiptPalletRowsMutation,
} from "@/graphql/codegen/graphql"
import { UpdateGoodsReceiptPalletRowsDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseUpdateGoodsReceiptPalletRowsReturn {
  update: (input: GoodsReceiptPalletRowUpdateInput[]) => Promise<ActionOutcome<GoodsReceiptPalletRow[]>>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateGoodsReceiptPalletRows = (onSuccess: () => void): UseUpdateGoodsReceiptPalletRowsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateGoodsReceiptPalletRowsMutation,
    GoodsReceiptMutationUpdatePalletRowArgs
  >(UpdateGoodsReceiptPalletRowsDocument)

  const update = async (input: GoodsReceiptPalletRowUpdateInput[]): Promise<ActionOutcome<GoodsReceiptPalletRow[]>> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.goodsReceipt.updatePalletRow
    const hasError = error || output?.__typename === "GoodsReceiptPalletRowUpdateFailure"

    if (hasError) {
      const errorMessage =
        error?.message ?? (output as GoodsReceiptPalletRowUpdateFailure).error ?? "Failed to update pallet rows"
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      return {
        error: new Error(errorMessage),
      }
    }

    notify({
      type: NotificationType.Success,
      content: "Pallet rows successfully updated",
    })
    onSuccess()

    return {
      data: (output as GoodsReceiptPalletRowUpdateSuccess).goodsReceiptPalletRows ?? [],
    }
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
